import { forwardRef, ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
    'inline-flex max-w-full items-center justify-center gap-2 rounded-[11px] text-center text-sm font-bold ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-tg-teal/40 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] sm:whitespace-nowrap',
    {
        variants: {
            variant: {
                default: 'bg-tg-teal text-white hover:bg-tg-teal-dk',
                destructive:
                    'bg-tg-danger text-white hover:opacity-90',
                outline:
                    'border border-tg-border bg-tg-surface-2 text-tg-ink hover:border-tg-teal hover:bg-white',
                ghost: 'text-tg-muted hover:bg-tg-surface-2 hover:text-tg-ink',
                secondary:
                    'border border-tg-border bg-tg-surface-2 text-tg-ink hover:border-tg-teal hover:bg-white',
            },
            size: {
                default: 'h-10 px-4 py-2',
                // Touch screens get a 40px floor via min-h/min-w (never fights a caller's h-*).
                sm: 'h-8 px-3 text-xs [@media(pointer:coarse)]:min-h-10',
                lg: 'h-12 px-6 text-base',
                icon: 'h-9 w-9 [@media(pointer:coarse)]:min-h-10 [@media(pointer:coarse)]:min-w-10',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

export interface ButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, ...props }, ref) => (
        <button
            ref={ref}
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    ),
);
Button.displayName = 'Button';
